// The "add artworks to this tour" affordance: lets a non-technical user pick from
// artifacts NOT already in the tour and add them one at a time (which appends the
// tour's slug to each chosen artifact's tourTags). Rendered inline by the FROZEN
// TourDetailView; do NOT change the props interface — see CONTRACT.md.
//
// Implementation notes:
//   - `candidates` are the artifacts not yet in the tour (already filtered),
//     newest first — render as-is; we only fuzzy-FILTER them for search.
//   - onAdd(id) adds one artifact to the tour; it's async and the parent refreshes,
//     so a successfully-added candidate drops out of `candidates` on the next
//     render (its row unmounts). We disable a row while its own add is in flight
//     ("Adding…") and disable every Add button while the global `busy` flag is set.
//   - onClose() dismisses the panel.
//   - A rejected add is surfaced inline on its row (the parent doesn't show errors).
//   - Styles are co-located in AddArtworksPanel.css (imported below), NOT in
//     styles.css.

import { useMemo, useState } from "react";
import Fuse from "fuse.js";
import { imageUrl } from "../config";
import type { Artifact } from "../types";
import { artifactLabel, tourTitle } from "../tours";
import { ArtImage } from "./ArtImage";
import "./AddArtworksPanel.css";

export interface AddArtworksPanelProps {
  slug: string;
  candidates: Artifact[];
  onAdd: (artifactId: string) => Promise<void>;
  onClose: () => void;
  busy?: boolean;
}

export function AddArtworksPanel({ slug, candidates, onAdd, onClose, busy }: AddArtworksPanelProps) {
  // Controlled local search text; fuzzy-filters `candidates` by title/artist/notes.
  const [query, setQuery] = useState("");
  // The id whose add is currently in flight (only one row can be mid-add at a
  // time in practice, but a Set keeps it robust if taps overlap).
  const [pending, setPending] = useState<Set<string>>(new Set());
  // Per-artifact "couldn't add" message, shown inline on the row.
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Build the fuzzy index over the current candidate pool. Recomputes only when
  // the candidate list changes (e.g. after a successful add shrinks it).
  const fuse = useMemo(
    () =>
      new Fuse(candidates, {
        keys: ["title", "artist", "notes"],
        threshold: 0.4, // forgiving but not noisy — typo-tolerant title/artist match
        ignoreLocation: true,
      }),
    [candidates],
  );

  // Apply the search. Empty query → the candidates as-is (already newest-first).
  const visible = useMemo(() => {
    const q = query.trim();
    if (!q) return candidates;
    return fuse.search(q).map((r) => r.item);
  }, [query, candidates, fuse]);

  // Add one artifact: mark its row pending, await the parent, and surface any
  // rejection inline. On success the parent refreshes and this row unmounts, so
  // we only need to clean up the pending/error state on failure.
  const handleAdd = async (id: string) => {
    setErrors((prev) => {
      if (!prev[id]) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setPending((prev) => new Set(prev).add(id));
    try {
      await onAdd(id);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Couldn’t add — try again.";
      setErrors((prev) => ({ ...prev, [id]: message }));
    } finally {
      setPending((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  const hasCandidates = candidates.length > 0;

  return (
    <div className="add-panel" role="region" aria-label={`Add artworks to ${tourTitle(slug)}`}>
      <div className="add-panel__head">
        <h2 className="add-panel__title">
          Add to “{tourTitle(slug)}”
        </h2>
        <button type="button" className="btn btn--small" onClick={onClose}>
          Close
        </button>
      </div>

      {/* Search — optional when the pool is tiny, but cheap and helpful at ~100. */}
      {hasCandidates && (
        <div className="add-panel__search">
          <input
            type="search"
            className="add-panel__search-input"
            placeholder="Search by title or artist…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            // iOS Safari: keep the keyboard plain (no autocorrect mangling names).
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            enterKeyHint="search"
          />
        </div>
      )}

      {/* Empty states: nothing left to add, vs. a search with no matches. */}
      {!hasCandidates ? (
        <p className="add-panel__empty">Every artwork is already in this tour.</p>
      ) : visible.length === 0 ? (
        <p className="add-panel__empty">No artworks match your search.</p>
      ) : (
        <ul className="add-panel__list">
          {visible.map((art) => {
            // Mirror TourListView's cover resolution: thumbnail → canonical, with
            // the original photo as the <ArtImage> fallback.
            const src = imageUrl(art.thumbnailKey) ?? imageUrl(art.canonicalImageKey);
            const fallbackSrc = imageUrl(art.originalImageKey);
            const label = artifactLabel(art);
            const isPending = pending.has(art.id);
            const error = errors[art.id];
            return (
              <li key={art.id} className="add-panel__row">
                <div className="add-panel__thumb">
                  <ArtImage
                    src={src}
                    fallbackSrc={fallbackSrc}
                    alt={label}
                    className="add-panel__img"
                  />
                </div>
                <div className="add-panel__info">
                  <span className="add-panel__name">{label}</span>
                  {/* Only show the artist line when it isn't already the label. */}
                  {art.artist && art.artist.trim() && art.artist.trim() !== label && (
                    <span className="add-panel__artist">{art.artist.trim()}</span>
                  )}
                  {error && (
                    <span className="add-panel__error" role="alert">
                      {error}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn--small btn--primary add-panel__add"
                  // Disable while this row is mid-add, or while ANY membership
                  // mutation is in flight (the shared `busy` flag).
                  disabled={isPending || busy}
                  onClick={() => {
                    void handleAdd(art.id);
                  }}
                >
                  {isPending ? "Adding…" : error ? "Retry" : "Add"}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
